import { useState } from 'react';
import { motion } from "framer-motion";
import Section from "./Section";
import { pricing } from "../constants";

const Pricing = ({ onGetStarted }) => {
  const [isYearly, setIsYearly] = useState(true);

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0,
      y: 20
    },
    visible: { 
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  const calculatePrice = (monthlyPrice) => {
    if (isYearly) {
      const yearlyPrice = monthlyPrice * 12;
      const discountedPrice = yearlyPrice * 0.9; // 10% discount
      return (discountedPrice / 12).toFixed(2);
    }
    return monthlyPrice;
  };

  return (
    <Section id="pricing" className="overflow-hidden">
      <div className="container">
        <div className="text-center mb-12 md:mb-20">
          <h2 className="h2 mb-4 text-textDark dark:text-gray-50">Simple, transparent pricing</h2>
          <p className="body-1 text-gray-600 dark:text-gray-400 md:max-w-2xl mx-auto">
            Get started with our straightforward pricing plans. No hidden fees, just powerful AI tools at your fingertips.
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center mt-8 space-x-4">
            <span className={`text-sm font-medium ${!isYearly ? 'text-indigo-600' : 'text-gray-500'}`}>Monthly</span>
            <button
              onClick={() => setIsYearly(!isYearly)}
              className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              style={{ backgroundColor: isYearly ? '#4F46E5' : '#E5E7EB' }}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isYearly ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${isYearly ? 'text-indigo-600' : 'text-gray-500'}`}>
              Yearly
              {isYearly && <span className="ml-1.5 text-xs text-green-500">(Save 10%)</span>}
            </span>
          </div>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
        >
          {pricing.map((item, index) => (
            <motion.div
              key={item.id}
              variants={cardVariants}
              className={`card group p-6 lg:p-8 ${
                item.popular ? 'gradient-border' : ''
              }`}
            >
              <div className="flex flex-col h-full">
                {item.popular && (
                  <div className="absolute top-0 right-6 transform -translate-y-1/2">
                    <div className="inline-block px-4 py-1 text-sm font-semibold tracking-wide text-white bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full shadow-md">
                      Popular
                    </div>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="h4 mb-2 text-textDark dark:text-gray-50">{item.title}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{item.description}</p>
                </div>

                <div className="mb-6">
                  <div className="flex items-end mb-2">
                    {item.price === 0 ? (
                      <span className="text-5xl font-bold text-textDark dark:text-gray-50">
                        Free
                      </span>
                    ) : item.price === null ? (
                      <span className="text-5xl font-bold text-textDark dark:text-gray-50">
                        Custom
                      </span>
                    ) : (
                      <>
                        <span className="text-4xl font-bold text-textDark dark:text-gray-50">$</span>
                        <span className="text-5xl font-bold text-textDark dark:text-gray-50">
                          {calculatePrice(item.price)}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">/ month</span>
                      </>
                    )}
                  </div>
                  {isYearly && item.price !== 0 && item.price !== null && (
                    <p className="text-sm text-green-500">
                      ${(calculatePrice(item.price) * 12).toFixed(2)} billed yearly
                    </p>
                  )}
                </div>

                <ul className="space-y-3 mb-6 flex-grow">
                  {item.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-gray-600 dark:text-gray-400">
                      <svg
                        className="w-4 h-4 mr-3 text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={onGetStarted}
                  className={`button ${
                    item.popular
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:from-indigo-600 hover:to-purple-600'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                  } w-full py-4 rounded-lg transition-all duration-200`}
                >
                  Get Started
                </button>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </Section>
  );
};

export default Pricing;
